package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.Plan
import dev.lingvo.site.ds.LCaretMark
import dev.lingvo.site.ds.LChip
import dev.lingvo.site.ds.LReveal
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul



/**
 * Every format, and the count the section quotes is derived from this list.
 *
 * It read "Ten formats" beside a list of ten while the product supported eleven — `.stringsdict`
 * was missing, which is the one that carries Apple plurals and therefore the least good one to
 * leave off a page arguing that plurals are handled properly.
 */
private val supportedFormats = listOf(
    "Android XML",
    "iOS .strings",
    "iOS .stringsdict",
    "Apple String Catalog",
    "JSON · nested",
    "JSON · flat",
    ".properties",
    "YAML",
    "CSV",
    "XLIFF",
    "PO",
)

val MarketingSectionStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = Space.Section, leftRight = Space.LG)
    }
    Breakpoint.MD {
        Modifier.padding(topBottom = Space.Hero, leftRight = Space.XXXL)
    }
}





/** [MarketingSection]'s band without the tall breakpoint step, for the formats strip. */
val CompactSectionStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(topBottom = Space.Section, leftRight = Space.LG)
}

val FormatsListStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("justify-content", "center")
            property("list-style", "none")
            property("margin", "0")
            property("padding", "0")
        }
}
// LandingSections, ProofSection, FormatsStrip and CloseSection lived here until the Polyglot
// redesign. The landing page is now a stack of Bands composed directly in Index.kt, and each of
// those four had a successor: FormatsBand, PluralsBand, CloseBand and the bands around them.
//
// What remains in this file is the two things the *other* marketing pages still use —
// MarketingSection and SectionHeadingPublic — which is why the file survives its own name.

/**
 * A marketing section: the outer band, the centred column, and the entrance.
 *
 * Sections used to differ only in their contents but each repeated the band-plus-column pair, which
 * is how one of them ends up with different padding after a hurried edit. Putting the reveal here
 * too means a new section animates like the others without anybody remembering to ask for it.
 */
@Composable
fun MarketingSection(
    id: String,
    labelledBy: String,
    /** Hung in the margin beside the section, the way a correction is written next to set type. */
    marginNote: String? = null,
    /** Keeps the narrow padding at every width, for a strip that answers rather than argues. */
    compact: Boolean = false,
    /** Ends the page on a different ground, so the close reads as a close. */
    sunken: Boolean = false,
    content: @Composable () -> Unit,
) {
    val sunkenBackground = ColorMode.current.toSitePalette().surfaceSunken
    Section(
        (if (compact) CompactSectionStyle.toModifier() else MarketingSectionStyle.toModifier())
            .thenIf(sunken) { Modifier.backgroundColor(sunkenBackground) }
            .toAttrs {
            id(id)
            attr("aria-labelledby", labelledBy)
        },
    ) {
        LReveal(Modifier.fillMaxWidth()) {
            Div(MarketingSectionInnerStyle.toModifier().toAttrs()) {
                // The margin. Empty on narrow screens, where the note collapses into the content
                // column with everything else — a marginal annotation needs a margin to be in.
                Div(MarginRailStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
                    if (marginNote != null) {
                        Div(MarginNoteStyle.toModifier().toAttrs()) {
                            // Muted, not ochre. The one accent on this page now lives inside the
                            // proof panel, on the missing plural — and an accent is only an accent
                            // while it is the only one.
                            LCaretMark(Modifier.color(ColorMode.current.toSitePalette().textMuted))
                            Span(Modifier.toAttrs()) { Text(marginNote) }
                        }
                    }
                }
                Column(Modifier.fillMaxWidth().gap(Space.XXXL)) {
                    content()
                }
            }
        }
    }
}

/**
 * The spine.
 *
 * A continuous hairline down the left of the content column, with each section's note hung in the
 * margin beside it. This is what makes the page one galley rather than five stacked sections: the
 * rule runs through all of them, so the eye reads a document rather than a sequence of cards.
 *
 * Below the breakpoint the rail collapses to nothing and the notes disappear entirely rather than
 * stacking above their sections — a marginal note with no margin is just another label, and the page
 * already has enough of those.
 */
/**
 * A centred single column, for pages with no margin rail.
 *
 * [MarketingSectionInnerStyle] is a two-column **grid** — an 11rem margin rail beside the content —
 * and passing it to a `Column` does not make it one column. Every child becomes a grid item, so they
 * alternate: the first lands in the 176px rail, the second in the content column, the third back in
 * the rail. On `/pricing` that crushed the intro and one whole section into a strip two words wide
 * while the plan cards sat beside them, and it read as a page that had simply collapsed.
 *
 * Pages that want the galley spine use [MarketingSection]. Pages that just want centred content use
 * this.
 */
val MarketingPageColumnStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxWidth(Space.ContentMax)
        .gap(Space.XXXL)
        .styleModifier { property("margin", "0 auto") }
}

val MarketingSectionInnerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(Space.ContentMax)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("margin", "0 auto")
            }
    }
    Breakpoint.LG {
        Modifier.styleModifier {
            property("grid-template-columns", "11rem minmax(0, 1fr)")
            property("column-gap", Space.XXL.toString())
        }
    }
}

val MarginRailStyle = CssStyle {
    base {
        Modifier.styleModifier { property("display", "none") }
    }
    Breakpoint.LG {
        Modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "block")
                property("border-right", "1px solid ${colorMode.toSitePalette().border}")
                property("min-height", "100%")
            }
    }
}

val MarginNoteStyle = CssStyle.base {
    Modifier
        .monoFont()
        .padding(right = Space.MD, top = 6.px)
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Micro)
        .lineHeight(1.5)
        .styleModifier {
            property("display", "flex")
            property("gap", Space.XS.toString())
            property("align-items", "flex-start")
            property("justify-content", "flex-end")
            property("text-align", "right")
        }
}

/**
 * Shared by every marketing section, so their rhythm cannot drift apart.
 *
 * The eyebrow is gone from the signature, not just unused. Every section had one, and on the formats
 * band the eyebrow ("Keep your files") and the title ("Your files, unchanged") were the same sentence
 * twenty pixels apart — which is what an optional decorative slot eventually becomes. Deleting the
 * parameter is what stops it coming back.
 *
 * [description] is nullable because the landing page passes none and the pricing blocks still need to
 * frame their tables.
 */
@Composable
internal fun SectionHeadingPublic(
    id: String,
    title: String,
    description: String? = null,
    size: CSSLengthNumericValue = TypeScale.Heading,
    centred: Boolean = false,
) {
    Column(
        Modifier
            .maxWidth(if (centred) 52.cssRem else 42.cssRem)
            .gap(Space.SM)
            .thenIf(centred) {
                Modifier.styleModifier {
                    property("margin", "0 auto")
                    property("text-align", "center")
                }
            },
    ) {
        H2(
            Modifier
                .displayFont()
                .color(ColorMode.current.toSitePalette().textPrimary)
                .fontSize(size)
                .fontWeight(700)
                .lineHeight(TypeScale.DisplayLineHeight)
                .margin(0.px)
                .styleModifier { property("text-wrap", "balance") }
                .toAttrs { id(id) },
        ) {
            Text(title)
        }
        if (description != null) {
            P(
                Modifier
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.Body)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .margin(0.px)
                    .toAttrs(),
            ) {
                Text(description)
            }
        }
    }
}
